import React, { useRef, useEffect, useState } from "react";
import {
  Typography, Paper, Box, Divider,
  IconButton, Tooltip, Chip, InputBase,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import Draggable from "react-draggable";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import RemoveIcon from "@mui/icons-material/Remove";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import SearchIcon from "@mui/icons-material/Search";

const SUGGESTIONS = [
  { label: 'Handle objection', prompt: 'The customer just raised an objection. Provide a confident and empathetic response to address their concern.' },
  { label: 'Ask timeline', prompt: 'Ask the customer about their decision timeline and next steps in a natural way.' },
  { label: 'Close', prompt: 'Guide the conversation toward closing. Summarize the value and ask for commitment.' },
];

const formatTime = (ts) =>
  new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

const PulsingDots = ({ color }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, height: 18 }}>
    {[0, 1, 2].map((i) => (
      <Box
        key={i}
        sx={{
          width: 6, height: 6, borderRadius: "50%",
          backgroundColor: color,
          "@keyframes pulse-dot": {
            "0%, 80%, 100%": { opacity: 0.2, transform: "scale(0.75)" },
            "40%": { opacity: 1, transform: "scale(1)" },
          },
          animation: "pulse-dot 1.2s ease-in-out infinite",
          animationDelay: `${i * 0.2}s`,
        }}
      />
    ))}
  </Box>
);

const Waveform = ({ color }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: "3px", height: 16 }}>
    {[0.5, 1, 0.7, 1.2, 0.8, 1, 0.6].map((h, i) => (
      <Box
        key={i}
        sx={{
          width: 3,
          height: `${h * 12}px`,
          borderRadius: "2px",
          backgroundColor: color,
          "@keyframes wave-bar": {
            "0%, 100%": { transform: "scaleY(0.4)" },
            "50%": { transform: "scaleY(1)" },
          },
          animation: `wave-bar ${0.7 + i * 0.08}s ease-in-out infinite`,
          animationDelay: `${i * 0.09}s`,
        }}
      />
    ))}
  </Box>
);

const TELEPROMPTER_FONT = "'SF Pro Display', 'Segoe UI', Roboto, sans-serif";
const TRANSCRIPT_FONT = "'SF Pro Text', 'Segoe UI', Roboto, sans-serif";

const FloatingAssistantOverlay = ({ events = [], drawerWidth, appBarHeight, onSendPrompt }) => {
  const theme = useTheme();
  const nodeRef = useRef(null);
  const transcriptRef = useRef(null);
  const [isMinimized, setIsMinimized] = useState(false);
  const [copied, setCopied] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [silenceSeconds, setSilenceSeconds] = useState(0);

  const overlayWidth = 480;
  const overlayHeight = 560;

  const agentColor = theme.palette.categoricalColors.metric1;
  const customerColor = theme.palette.teamColors?.tealBlue || theme.palette.primary.main;

  useEffect(() => {
    if (!isMinimized) {
      const el = transcriptRef.current;
      if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    }
  }, [events.length, isMinimized]);

  useEffect(() => {
    const lastTs = events[events.length - 1]?.ts;
    if (!lastTs) { setSilenceSeconds(0); return; }
    const tick = () => setSilenceSeconds(Math.floor((Date.now() - lastTs) / 1000));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [events]);

  if (!events || events.length === 0) return null;

  const lastAgent = [...events].reverse().find((e) => e.type === "agent_response");
  const isPending = lastAgent?.status === "speaking" || lastAgent?.status === "transcribing";

  const isCustomerSpeaking = events[events.length - 1]?.type === "customer_speech" &&
    events[events.length - 1]?.status === "speaking";

  const handleCopy = () => {
    if (!lastAgent?.text) return;
    navigator.clipboard.writeText(lastAgent.text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const filteredEvents = events.filter(e =>
    (e.type === "agent_response" || e.type === "customer_speech") &&
    (!searchTerm || (e.text || '').toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <Draggable
      nodeRef={nodeRef}
      handle=".assistant-handle"
      defaultPosition={{ x: drawerWidth + 10, y: appBarHeight + 50 }}
      bounds={{
        left: drawerWidth + 10,
        top: appBarHeight + 50,
        right: window.innerWidth - overlayWidth,
        bottom: window.innerHeight - (isMinimized ? 44 : overlayHeight),
      }}
    >
      <Paper
        ref={nodeRef}
        elevation={6}
        sx={{
          position: "fixed",
          width: overlayWidth,
          height: isMinimized ? "auto" : overlayHeight,
          borderRadius: 2,
          backgroundColor: alpha(theme.palette.background.paper, 0.6),
          backdropFilter: "blur(16px)",
          overflow: "hidden",
          zIndex: theme.zIndex.drawer + 2,
          display: "flex",
          flexDirection: "column",
          transition: "height 0.2s ease",
        }}
      >
        {/* Header */}
        <Box
          className="assistant-handle"
          sx={{
            backgroundColor: theme.palette.teamColors.rose,
            color: theme.palette.primary.contrastText,
            px: 1.5,
            py: 0.75,
            display: "flex",
            alignItems: "center",
            gap: 1,
            cursor: "move",
            userSelect: "none",
            flexShrink: 0,
          }}
        >
          <DragIndicatorIcon fontSize="small" />
          <Typography variant="subtitle2" fontWeight={600} sx={{ flex: 1 }}>
            AI Assistant
          </Typography>

          {silenceSeconds >= 5 && (
            <Tooltip title="Silence duration">
              <Typography
                variant="caption"
                sx={{
                  fontSize: "0.68rem",
                  color: alpha(theme.palette.primary.contrastText, 0.75),
                  bgcolor: alpha(theme.palette.common.black, 0.2),
                  px: 0.8, py: 0.2,
                  borderRadius: 1,
                  fontFamily: "'SF Mono', 'Fira Code', monospace",
                }}
              >
                {silenceSeconds}s silence
              </Typography>
            </Tooltip>
          )}

          <Tooltip title="Search transcript">
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); setSearchOpen(v => !v); setSearchTerm(''); }}
              sx={{ color: alpha(theme.palette.primary.contrastText, 0.8), p: 0.3 }}
            >
              <SearchIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>

          <Tooltip title={isMinimized ? "Expand" : "Minimize"}>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); setIsMinimized(v => !v); }}
              sx={{ color: alpha(theme.palette.primary.contrastText, 0.8), p: 0.3 }}
            >
              {isMinimized
                ? <OpenInFullIcon sx={{ fontSize: 14 }} />
                : <RemoveIcon sx={{ fontSize: 14 }} />
              }
            </IconButton>
          </Tooltip>
        </Box>

        {!isMinimized && (
          <>
            {/* Teleprompter zone */}
            <Box
              sx={{
                flex: "0 0 280px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                px: 2.5,
                py: 2,
                gap: 1.5,
                backgroundColor: alpha(agentColor, 0.06),
                position: "relative",
              }}
            >
              <AutoAwesomeIcon sx={{ fontSize: 18, color: alpha(agentColor, 0.6) }} />

              {!lastAgent ? (
                <Typography
                  variant="body2"
                  sx={{
                    color: alpha(theme.palette.common.white, 0.25),
                    fontStyle: "italic",
                    textAlign: "center",
                    fontSize: "0.95rem",
                    fontFamily: TELEPROMPTER_FONT,
                    fontWeight: 300,
                    letterSpacing: "0.01em",
                  }}
                >
                  Waiting for agent response…
                </Typography>
              ) : isPending ? (
                <PulsingDots color={alpha(agentColor, 0.8)} />
              ) : (
                <Typography
                  key={lastAgent.id}
                  variant="body1"
                  sx={{
                    color: theme.palette.common.white,
                    textAlign: "center",
                    lineHeight: 1.7,
                    fontSize: "1.1rem",
                    fontWeight: 300,
                    fontFamily: TELEPROMPTER_FONT,
                    letterSpacing: "0.015em",
                    whiteSpace: "pre-wrap",
                    "@keyframes fade-in-up": {
                      from: { opacity: 0, transform: "translateY(5px)" },
                      to: { opacity: 1, transform: "translateY(0)" },
                    },
                    animation: "fade-in-up 0.3s ease-out",
                  }}
                >
                  {lastAgent.text}
                </Typography>
              )}

              {lastAgent && !isPending && (
                <Tooltip title={copied ? "Copied!" : "Copy response"}>
                  <IconButton
                    size="small"
                    onClick={handleCopy}
                    sx={{
                      position: "absolute",
                      bottom: 8, right: 8,
                      color: copied ? agentColor : alpha(theme.palette.common.white, 0.3),
                      "&:hover": { color: alpha(theme.palette.common.white, 0.7) },
                      transition: "color 0.2s",
                    }}
                  >
                    {copied
                      ? <CheckIcon sx={{ fontSize: 14 }} />
                      : <ContentCopyIcon sx={{ fontSize: 14 }} />
                    }
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            {/* Suggestion chips */}
            <Box
              sx={{
                display: "flex",
                gap: 0.75,
                px: 1.5,
                py: 0.75,
                flexWrap: "wrap",
                backgroundColor: alpha(theme.palette.background.paper, 0.3),
                borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.06)}`,
              }}
            >
              {SUGGESTIONS.map((s) => (
                <Chip
                  key={s.label}
                  label={s.label}
                  size="small"
                  onClick={() => onSendPrompt?.(s.prompt)}
                  sx={{
                    fontSize: "0.72rem",
                    height: 22,
                    fontFamily: TRANSCRIPT_FONT,
                    backgroundColor: alpha(agentColor, 0.12),
                    color: alpha(theme.palette.common.white, 0.75),
                    border: `1px solid ${alpha(agentColor, 0.25)}`,
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: alpha(agentColor, 0.25),
                      color: theme.palette.common.white,
                    },
                    transition: "all 0.15s ease",
                  }}
                />
              ))}
            </Box>

            <Divider sx={{ borderColor: alpha(theme.palette.common.white, 0.08) }}>
              {searchOpen ? (
                <InputBase
                  autoFocus
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search transcript…"
                  sx={{
                    fontSize: "0.72rem",
                    color: alpha(theme.palette.common.white, 0.7),
                    px: 1,
                    "& input": { padding: 0 },
                    width: 160,
                  }}
                />
              ) : (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, px: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{ fontSize: "0.62rem", color: alpha(theme.palette.common.white, 0.3) }}
                  >
                    Transcript
                  </Typography>
                  {isCustomerSpeaking && <Waveform color={alpha(customerColor, 0.7)} />}
                </Box>
              )}
            </Divider>

            {/* Transcript strip */}
            <Box
              ref={transcriptRef}
              sx={{
                flex: 1,
                overflowY: "auto",
                px: 1.5,
                py: 1,
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
                "&::-webkit-scrollbar": { width: 6 },
                "&::-webkit-scrollbar-thumb": {
                  background: alpha(theme.palette.common.white, 0.12),
                  borderRadius: 99,
                },
              }}
            >
              {filteredEvents.map((e) => {
                const isAgent = e.type === "agent_response";
                const dotColor = isAgent ? agentColor : customerColor;
                const pending = e.status === "speaking" || e.status === "transcribing";

                return (
                  <Box key={e.id} sx={{ display: "flex", alignItems: "flex-start", gap: 0.75 }}>
                    <Box
                      sx={{
                        width: 5, height: 5,
                        borderRadius: "50%",
                        backgroundColor: alpha(dotColor, 0.75),
                        mt: "5px",
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: "0.85rem",
                        fontFamily: TRANSCRIPT_FONT,
                        fontWeight: 300,
                        letterSpacing: "0.01em",
                        color: alpha(theme.palette.common.white, pending ? 0.3 : 0.85),
                        lineHeight: 1.4,
                        flex: 1,
                        fontStyle: pending ? "italic" : "normal",
                      }}
                    >
                      <Box component="span" sx={{ color: alpha(dotColor, 0.85), fontWeight: 500, mr: 0.4 }}>
                        {isAgent ? "Agent" : "Customer"}
                      </Box>
                      {pending ? "…" : e.text}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ fontSize: "0.75rem", color: alpha(theme.palette.common.white, 0.2), flexShrink: 0, mt: "2px" }}
                    >
                      {formatTime(e.ts)}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </>
        )}
      </Paper>
    </Draggable>
  );
};

export default FloatingAssistantOverlay;
